import Decimal from "decimal.js";
/**
 * Todo el dinero se maneja como string decimal, nunca como number.
 *
 * Un `0.1 + 0.2` en float produce 0.30000000000000004 y el SAT rechaza el
 * comprobante porque los totales no cuadran al centavo. En Postgres las
 * columnas son numeric; aquí usamos decimal.js; en la frontera con el PAC
 * siempre serializamos a string con el número exacto de decimales.
 */
Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_UP });
/** Decimales de los importes monetarios. Depende de la moneda del CFDI. */
export const DECIMALES_MONEDA = {
    MXN: 2,
    USD: 2,
    EUR: 2,
    XXX: 0,
};
/** El valor unitario admite hasta 6 decimales en el Anexo 20. */
export const DECIMALES_VALOR_UNITARIO = 6;
export const DECIMALES_CANTIDAD = 6;
export const dec = (v) => new Decimal(v ?? 0);
export const decimalesDe = (moneda) => DECIMALES_MONEDA[moneda] ?? 2;
/** Redondea a medio arriba, que es lo que espera el SAT. */
export const redondear = (v, decimales = 2) => dec(v).toDecimalPlaces(decimales, Decimal.ROUND_HALF_UP).toFixed(decimales);
export const sumar = (...valores) => valores.reduce((acc, v) => acc.plus(dec(v)), dec(0));
export const esCero = (v) => dec(v).isZero();
/**
 * Calcula un concepto. La base gravable es importe menos descuento, y cada
 * impuesto se redondea a nivel de concepto — no al final. El SAT valida que
 * la suma de los impuestos de los conceptos cuadre con el nodo Impuestos.
 */
export function calcularConcepto(c, moneda = "MXN") {
    const d = decimalesDe(moneda);
    const importe = dec(c.cantidad).times(dec(c.valorUnitario));
    const descuento = dec(c.descuento ?? 0);
    const base = importe.minus(descuento);
    const gravado = c.objetoImp === "02" || c.objetoImp === "03";
    const aplicar = (tasa) => gravado && tasa !== null && tasa !== undefined
        ? redondear(base.times(dec(tasa)), d)
        : null;
    return {
        importe: redondear(importe, d),
        descuento: redondear(descuento, d),
        base: redondear(base, d),
        trasladoIva: aplicar(c.tasaIva),
        retencionIva: aplicar(c.tasaRetIva),
        retencionIsr: aplicar(c.tasaRetIsr),
    };
}
/**
 * Totales del comprobante.
 *
 * SubTotal es la suma de importes ANTES de descuento — error clásico es
 * restarlo ahí. Total = SubTotal - Descuento + Trasladados - Retenidos.
 */
export function calcularTotales(conceptos, moneda = "MXN") {
    const d = decimalesDe(moneda);
    const subTotal = sumar(...conceptos.map((c) => c.importe));
    const descuento = sumar(...conceptos.map((c) => c.descuento));
    const trasladados = sumar(...conceptos.map((c) => c.trasladoIva ?? 0));
    const retenidos = sumar(...conceptos.map((c) => dec(c.retencionIva ?? 0).plus(c.retencionIsr ?? 0)));
    const total = subTotal.minus(descuento).plus(trasladados).minus(retenidos);
    return {
        subTotal: redondear(subTotal, d),
        descuento: redondear(descuento, d),
        totalImpuestosTrasladados: redondear(trasladados, d),
        totalImpuestosRetenidos: redondear(retenidos, d),
        total: redondear(total, d),
    };
}
export const formatearMoneda = (v, moneda = "MXN") => new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: moneda === "XXX" ? "MXN" : moneda,
    minimumFractionDigits: decimalesDe(moneda),
}).format(dec(v).toNumber());
//# sourceMappingURL=money.js.map